<template>
  <div class="card mb-0 border-0 rounded-0 bg-white add-user-card">
    <div class="card-header">
      <h3 class="text-black fw-semibold">Modifier un contrat client</h3>
    </div>
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <Form ref="formRef" @submit="editContrat" :validation-schema="contratSchema">
        <div class="row">
          <!-- Date du contrat -->
          <div class="col-md-5 mb-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="label-inline text-black fw-semibold mb-10">
                Date du contrat <span class="text-danger">*</span>
              </label>
              <Field
                name="dateContrat"
                type="date"
                class="form-control shadow-none fs-md-15 text-black"
              />
              <ErrorMessage name="dateContrat" class="text-danger" />
            </div>
          </div>
          <!-- Client -->
          <div class="col-md-7 mb-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="label-inline text-black fw-semibold mb-10">
                Client <span class="text-danger">*</span>
              </label>
              <Field name="client" v-slot="{ field }">
                <Multiselect
                  v-model="field.value"
                  v-bind="field"
                  :options="clientOptions"
                  :searchable="true"
                  track-by="label"
                  label="label"
                  placeholder="Sélectionner le client"
                />
              </Field>
              <ErrorMessage name="client" class="text-danger" />
            </div>
          </div>
          <!-- Durée de créance -->
          <div class="col-md-6 mb-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="label-inline text-black fw-semibold mb-10">
                Durée de créance (jours) <span class="text-danger">*</span>
              </label>
              <Field
                name="dureeCreance"
                type="number"
                class="form-control shadow-none fs-md-15 text-black"
                placeholder="Entrer la durée"
              />
              <ErrorMessage name="dureeCreance" class="text-danger" />
            </div>
          </div>
          <!-- Plafond de créance -->
          <div class="col-md-6 mb-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="label-inline text-black fw-semibold mb-10">
                Plafond de créance <span class="text-danger">*</span>
              </label>
              <Field
                name="plafond"
                type="number"
                class="form-control shadow-none fs-md-15 text-black"
                placeholder="Entrer le plafond"
              />
              <ErrorMessage name="plafond" class="text-danger" />
            </div>
          </div>
          <!-- Boutons -->
          <div class="col-md-12">
            <div class="d-flex align-items-center">
              <button
                ref="submitButton"
                class="btn btn-success me-3"
                type="submit"
                :disabled="isSubmitting"
              >
                Modifier le contrat
              </button>
              <router-link to="/contrats/liste-contrats" class="btn btn-danger">
                <i class="fa fa-times lh-1 me-1 position-relative top-2"></i>
                <span class="position-relative">Annuler</span>
              </router-link>
            </div>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import * as Yup from 'yup';
import ApiService from '@/services/ApiService';
import { error, success } from '@/utils/utils';
import { useRoute, useRouter } from 'vue-router';
import Multiselect from '@vueform/multiselect';

export default defineComponent({
  name: 'EditContrat',
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect,
  },
  setup() {
    // Validation schema
    const contratSchema = Yup.object().shape({
      dateContrat: Yup.date().required('La date du contrat est obligatoire'),
      client: Yup.string().required('Le client est obligatoire'),
      dureeCreance: Yup.number()
        .typeError('Veuillez entrer un nombre')
        .required('La durée de la créance est obligatoire')
        .min(1, 'La durée doit être supérieure à 0'),
      plafond: Yup.number()
        .typeError('Veuillez entrer un nombre')
        .required('Le plafond est obligatoire')
        .min(0, 'Le plafond doit être positif'),
    });

    // Reactive variables
    const formRef = ref(null);
    const clientOptions = ref<{ value: number; label: string }[]>([]);
    const submitButton = ref<HTMLButtonElement | null>(null);
    const isSubmitting = ref(false);
    const router = useRouter();
    const route = useRoute();
    const contratId = ref(0);

    // Fetch clients
    const fetchClient = async () => {
      try {
        const response = await ApiService.get('/all/clients');
        const clientsData = response.data.data;
        clientOptions.value = clientsData.map((client: any) => ({
          value: client.id,
          label: client.nomClient && client.prenomClient
            ? `${client.nomClient} ${client.prenomClient}`
            : `${client.raisonSociale}`,
        }));
      } catch (err) {
        error('Erreur lors du chargement des clients');
        console.error(err);
      }
    };

    // Fetch contract by ID
    const getContrat = async (id: number) => {
      try {
        const response = await ApiService.get(`/contrats/${id}`);
        const data = response.data.data;
        contratId.value = data.id;
        formRef.value.setFieldValue('dateContrat', data.dateContrat);
        formRef.value.setFieldValue('client', data.client.id.toString());
        formRef.value.setFieldValue('dureeCreance', data.dureeCreance);
        formRef.value.setFieldValue('plafond', data.plafond);
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors du chargement du contrat');
      }
    };

    // Submit edited contract
    const editContrat = async (values: any) => {
      if (submitButton.value) {
        submitButton.value.disabled = true;
        isSubmitting.value = true;
      }

      const payload = {
        id: contratId.value,
        dateContrat: values.dateContrat,
        client: { id: values.client },
        dureeCreance: Number(values.dureeCreance),
        plafond: Number(values.plafond),
      };

      try {
        const response = await ApiService.put(`/contrats/${payload.id}`, payload);
        if (response.data.code === 200) {
          success(response.data.message);
          router.push({ name: 'ListeContrat' });
        }
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors de la modification du contrat');
      } finally {
        if (submitButton.value) {
          submitButton.value.disabled = false;
          isSubmitting.value = false;
        }
      }
    };

    // Load data on mount
    onMounted(() => {
      fetchClient();
      if (route.params.id) {
        getContrat(parseInt(route.params.id as string));
      }
    });

    return {
      contratSchema,
      editContrat,
      formRef,
      clientOptions,
      submitButton,
      isSubmitting,
    };
  },
});
</script>

<style scoped>
.label-inline {
  display: flex;
  align-items: center;
  gap: 0.25rem; /* Space between label text and asterisk */
}

.text-danger {
  display: block;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}
</style>